import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { PagedList } from "../modules/shared/models/api-response.model";
import { AuthService } from "./auth.service";

const API_URL = environment.api;

export class LibraryUpload {
    libraryDocumentId: number;
    accountId: string;
    trimmedAccountId: string;
    documentName: string;
    documentType: string;
    documentTypeCode: string;
    submittedAt: Date;
    viewable: boolean;

    static fromJSON(obj): LibraryUpload {
        let temp = new LibraryUpload();
        Object.assign(temp, obj);

        temp.viewable = true;

        if (obj.submittedAt) {
            temp.submittedAt = new Date(obj.submittedAt + "Z");
        }

        return temp;
    }
}

export class LibraryType {
    name: string;
    code: string;

    static fromJSON(obj): LibraryUpload {
        let temp = new LibraryUpload();
        Object.assign(temp, obj);
        return temp;
    }
}

@Injectable()
export class DocumentLibraryService {
    constructor(
        private _http: HttpClient,
        private _authService: AuthService
    ) {}

    private get _defaultOptions() {
        return { headers: this._headers };
    }
    private get _headers(): HttpHeaders {
        let h = new HttpHeaders();
        h = h.set("Authorization", `bearer ${this._authService.accessToken}`);
        h = h.set("Content-Type", "application/json");
        return h;
    }

    uploadLibraryDocument(
        file: File,
        accountId: string,
        documentType: string
    ): Observable<any> {
        let h = this._headers;
        h = h.set("Content-Type", "multipart/form-data");
        h = h.delete("Content-Type");

        let options = { headers: h };
        let formData = new FormData();

        formData.append("file", file, file.name);
        formData.append("documentType", documentType);

        return this._http
            .post(
                `${API_URL}/api/v1/document-library/${accountId}/add-file`,
                formData,
                options
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    deleteLibraryDocument(
        accountId: string,
        documentType: string,
        filename: string
    ): Observable<any> {
        let formData = new FormData();
        return this._http
            .post(
                `${API_URL}/api/v1/document-library/${accountId}/remove-file/${documentType}/${filename}`,
                formData,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    editLibraryDocument(
        libraryDocumentId: number,
        accountId: string,
        documentTypeCode: string,
        documentName: string
    ): Observable<any> {
        let formData = {
            libraryDocumentId: libraryDocumentId,
            accountId: accountId,
            documentTypeCode: documentTypeCode,
            documentName: documentName
        };

        return this._http
            .post(
                `${API_URL}/api/v1/document-library/edit`,
                formData,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getLibraryDocuments(
        offset: number = 0,
        limit: number = 10,
        sort: string | null = null,
        search: string | null = null,
        accounts: string[] | null = null,
        types: string[] | null = null,
        submittedRange: any | null = null
    ): Observable<PagedList<LibraryUpload>> {
        let p = {
            offset: `${offset * limit}`,
            limit: `${limit}`
        };

        if (search) {
            p["filter"] = search;
        }

        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }

        if (types && types.length > 0) {
            p["document-types"] = types.join(",");
        }

        if (sort) {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        } else {
            p["sort"] = "-submittedAt";
        }

        if (submittedRange && submittedRange.begin && submittedRange.end) {
            p["start-at"] =
                submittedRange.begin.toISOString().split("T")[0] + "T00:00:00Z";
            p["end-at"] =
                submittedRange.end.toISOString().split("T")[0] +
                "T23:59:59.999Z";
        }

        let options = { headers: this._headers, params: p };

        return this._http
            .get(`${API_URL}/api/v1/document-library`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    res.offset = res.offset / res.limit;
                    res.data = res.data.map((elem) =>
                        LibraryUpload.fromJSON(elem)
                    );
                    return res;
                })
            );
    }

    listLibraryTypes(): Observable<LibraryType[]> {
        return this._http
            .get(`${API_URL}/api/v1/library/types`, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.map((elem) => LibraryType.fromJSON(elem));
                })
            );
    }

    addLibraryType(type: string) {
        let options = { headers: this._headers, observe: "response" } as any;

        return this._http
            .post(`${API_URL}/api/v1/library/types/${type}`, null, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.ok;
                })
            );
    }

    removeLibraryType(type: string) {
        let options = { headers: this._headers, observe: "response" } as any;

        return this._http
            .delete(`${API_URL}/api/v1/library/types/${type}`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.ok;
                })
            );
    }

    previewLibraryDocument(url: string) {
        let options = {
            headers: this._headers,
            responseType: "blob" as "json"
        } as any;
        return this._http
            .get(`${API_URL}/api/v1/document-library/${url}`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }
}
